import { jobProfileLabel, seedCatalog, type Person } from '@vigeo/shared';
import { NO_JOB_TITLE } from './staff.js';

/**
 * WHO THE COVERAGE MAP COUNTS, AND HOW (#804, Khalia's review of 2026-10-21).
 *
 * Office staff inflated the map's counts and the list, and coverage has to read per discipline. Full-time apart from PRN waits for
 * the fixed Full-time / Part-time / PRN choice of ADR-029 (#806): the free-text Schedule field cannot say it reliably.
 * Everything here is read from the person and the seeded catalog, with no request and nothing stored (hard rule 4).
 */

/** The job profiles the catalog marks "Direct care" (docs/seeded-catalog.md). A retired profile keeps its mark: a person can still hold one. */
const DIRECT_CARE: ReadonlySet<string> = new Set(seedCatalog.jobProfiles.filter((profile) => profile.directCare).map((profile) => profile.key));

/**
 * Whether a person sees patients: they hold at least one direct-care job profile. Office or administrative roles, and staff with no
 * role set, do not, so the map leaves them out of its counts and shading. They stay on the roster and in its list.
 */
export const seesPatients = (person: Pick<Person, 'jobProfileKeys'>): boolean => person.jobProfileKeys.some((key) => DIRECT_CARE.has(key));

/** A job profile's words, from the seeded catalog with no request (`jobProfileLabel`, #801). A key the catalog does not hold reads as itself. */
export const jobTitleLabel = (key: string): string => jobProfileLabel(key) ?? key;

/** The Job title filter's choice for staff with no job profile. Not a possible profile key: keys are lowercase words and hyphens. */
export const NO_ROLE_CHOICE = '(no role)';
export const NO_ROLE_LABEL = NO_JOB_TITLE;

/** Whether a person holds any of the chosen job titles (`NO_ROLE_CHOICE` is "holds none"). */
export function holdsJobTitle(person: Pick<Person, 'jobProfileKeys'>, chosen: readonly string[]): boolean {
  return chosen.some((choice) => (choice === NO_ROLE_CHOICE ? person.jobProfileKeys.length === 0 : person.jobProfileKeys.includes(choice)));
}

/** The job titles held on the roster, A to Z by their words, then "No role set" when somebody has none. */
export function jobTitleOptions(people: readonly Pick<Person, 'jobProfileKeys'>[]): { readonly key: string; readonly label: string }[] {
  const keys = new Set(people.flatMap((person) => person.jobProfileKeys));
  const options = [...keys].map((key) => ({ key, label: jobTitleLabel(key) })).sort((a, b) => a.label.localeCompare(b.label));
  return people.some((person) => person.jobProfileKeys.length === 0) ? [...options, { key: NO_ROLE_CHOICE, label: NO_ROLE_LABEL }] : options;
}

/**
 * Whether a person holds only non-clinical job titles (administrative or office): at least one job profile, and none of them direct care.
 * The Roster's list hides them by default, with a way to show them (Julio, 2026-10-21, #804). Staff with NO role set are not
 * non-clinical: they stay in the list, so HR can set their role.
 */
export const holdsOnlyNonClinical = (person: Pick<Person, 'jobProfileKeys'>): boolean => person.jobProfileKeys.length > 0 && !seesPatients(person);
